import React, { useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics';
import { AiSuggestionsEntryPointValue } from '@atlaskit/editor-common/analytics/ai-suggestions-entry-points';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics/types/enums';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import IconButton from '@atlaskit/button/icon/button';
import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';

import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';

import { reviewButtonMessages } from './reviewButtonMessages.ts';
import { AnalyticContexts } from './analyticsUtils.tsx';

interface ReviewButtonProps {
	onStartThinking?: () => void;
	fireAnalyticsEvent?: EditorAnalyticsAPI['fireAnalyticsEvent'];
	isDisabled: boolean;
	isVisible: boolean;
	// Renders a blue dot signalling unseen AI suggestions on this page.
	showNotificationDot?: boolean;
}

const containerStyles = css({
	display: 'inline-flex',
	position: 'relative',
});

const dotStyles = css({
	backgroundColor: token('color.icon.brand'),
	borderRadius: token('radius.full'),
	border: `${token('border.width.selected')} solid ${token('color.border.inverse')}`,
	display: 'block',
	height: token('space.075'),
	width: token('space.075'),
	pointerEvents: 'none',
	position: 'absolute',
	right: '3px',
	top: '3px',
});

const i18n = defineMessages({
	unseenSuggestionsLabel: {
		id: 'object-sidebar-components.review.button.unseen-suggestions.label',
		defaultMessage: 'New suggestions available',
		description:
			'Accessible label for the blue notification dot shown on the Review suggestions button when there are AI suggestions the user has not yet seen',
	},
});

const analyticsContext = {
	source: AnalyticContexts.Source,
	name: `${AnalyticContexts.Source}ReviewButton`,
	attributes: {
		source: AnalyticContexts.Source,
		name: `${AnalyticContexts.Source}ReviewButton`,
	},
};

export const ReviewButton = ({
	onStartThinking,
	fireAnalyticsEvent,
	isDisabled,
	isVisible,
	showNotificationDot = false,
}: ReviewButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const hasFiredExposureRef = useRef(false);

	useEffect(() => {
		if (!isVisible || !fireAnalyticsEvent || hasFiredExposureRef.current) {
			return;
		}

		fireAnalyticsEvent({
			action: ACTION.EXPOSED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			source: AnalyticContexts.Source,
			attributes: { entryPoint: AiSuggestionsEntryPointValue.OBJECT_SIDEBAR_CONTROL },
		});
		hasFiredExposureRef.current = true;
	}, [isVisible, fireAnalyticsEvent]);

	return (
		<Tooltip
			position="left"
			content={<FormattedMessage {...reviewButtonMessages.reviewSuggestionsLabel} />}
			delay={0}
		>
			<span css={containerStyles}>
				<IconButton
					icon={TextReviewIcon}
					appearance="subtle"
					label={formatMessage(reviewButtonMessages.reviewSuggestionsLabel)}
					testId="object-sidebar-review-button"
					shape={isRovoButtonEnabled ? 'default' : 'circle'}
					onClick={onStartThinking}
					isDisabled={isDisabled}
					analyticsContext={analyticsContext}
				/>
				{showNotificationDot && (
					<span
						css={dotStyles}
						data-testid="object-sidebar-review-button-notification-dot"
						role="status"
						aria-label={formatMessage(i18n.unseenSuggestionsLabel)}
					/>
				)}
			</span>
		</Tooltip>
	);
};
